import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Avatar,
  Badge,
  Button,
  Card,
  ErrorState,
  Input,
  Page,
  Skeleton,
} from '@indipia/ui-core';
import { billingApi } from '../api/billingApi';
import type { ReferralInvite } from '../api/billingApi';

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

const STATUS_BADGE: Record<string, { label: string; status: 'success' | 'info' }> = {
  FIRST_PAYMENT_DONE: { label: '전환완료', status: 'success' },
  JOINED: { label: '체험중', status: 'info' },
};

/**
 * C-8(추천인 초대 화면). 진입경로: C-2(SubscriptionManagePage)에서 링크 · 좌측 계정 메뉴(구독결제
 * 서브메뉴)에서 직접 진입도 가능 — 상단 PageHeader(뒤로가기 포함) 제거(사용자 요청, 2026-09-02)로
 * 페이지 자체에는 전용 뒤로가기가 없다.
 * <p>
 * "/u/r/{code}" 랜딩 라우트(apps/shell-web `ReferralLandingPage`)는 완료(2026-09-02, 경로는
 * 2026-09-21에 모바일 App Link 재사용을 위해 `/r/{code}`에서 변경) — 로그인 방문자는
 * PricingPlansPage의 `?ref=` 처리로 바로 이어지지만, ⚠️ 신규가입(C-1) 쪽에서 미로그인 방문자가
 * localStorage에 잠깐 들고 있던 코드를 실제로 꺼내 startTrial()에 넘기는 배선은 아직 없다(사용자
 * 결정으로 이번 스코프에서는 보류, 추후작업_TODO.md 참고).
 */
/** 카카오 브랜드 배색(D-07 §5.3 외부 채널 브랜드색 예외 — apps/shell-web SnsBtn.tsx와 동일 값,
 *  토큰화하지 않는다 — 임의로 바꾸면 브랜드 가이드 위반). */
const KAKAO_BRAND = { iconBg: 'var(--brand-kakao-symbol)', iconColor: 'var(--brand-kakao-container)', bg: 'var(--brand-kakao-container)', label: 'var(--brand-kakao-label)' }; // D-07 §5.3 브랜드 토큰

export function ReferralInvitePage() {
  const [copied, setCopied] = useState(false);
  const [resending, setResending] = useState<'kakao' | 'sms' | null>(null);

  const codeQuery = useQuery({
    queryKey: ['billing', 'referralCode'],
    queryFn: async ({ signal }) => (await billingApi.getMyReferralCode(signal)).data.data,
  });

  const statusQuery = useQuery({
    queryKey: ['billing', 'referralStatus'],
    queryFn: async ({ signal }) => (await billingApi.getReferralStatus(signal)).data.data,
  });

  // 경로가 `/u/`로 시작해야 모바일 앱의 검증된 Android App Link intent-filter(pathPrefix="/u/")가
  // AndroidManifest.xml 수정 없이 이 링크를 그대로 앱으로 받을 수 있다(2026-09-21 변경 — 이전엔
  // `/r/{code}`였음, ReferralLandingPage 라우트·BE ReferralService.referralLink와 함께 맞춤).
  const shareUrl = codeQuery.data ? `${window.location.origin}/u/r/${codeQuery.data.codeValue}` : null;

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error('복사에 실패했어요. 링크를 직접 선택해서 복사해주세요.');
    }
  };

  // 재발송(확인용) — 클릭한 버튼 그대로 BE에 channel(KAKAO/SMS)로 넘긴다(2026-09-16 — 예전엔 두
  // 버튼이 항상 같은 결과였음). 실제로 그 채널만 나가려면 Hermes 정책에 채널별 조건 매칭이 등록돼
  // 있어야 한다(billingApi.resendReferralLink 주석 참고).
  const handleResend = async (channel: 'kakao' | 'sms') => {
    setResending(channel);
    try {
      await billingApi.resendReferralLink(channel === 'kakao' ? 'KAKAO' : 'SMS');
      toast.success('추천 링크를 다시 보내드렸어요. 잠시 후 도착해요');
    } catch {
      toast.error('재발송에 실패했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setResending(null);
    }
  };

  const converted = statusQuery.data?.convertedCount ?? 0;
  const target = statusQuery.data?.milestoneTarget ?? 3;
  const remaining = Math.max(0, target - converted);

  return (
    <Page width="md">
      {/* 히어로 (C-8-2) — 정적 텍스트 */}
      <div className="mt-06 text-center">
        <p className="text-caption font-semibold uppercase tracking-wide text-group">REFERRAL</p>
        <h2 className="mt-02 text-title-lg font-bold text-text">
          사장님 한 분을 초대하면
          <br />
          둘 다 한 달을 더 써요
        </h2>
        <p className="mt-02 text-body-sm text-text-muted">
          추천받은 사장님이 유료로 전환하면, 추천한 사장님께 자동으로 무료 개월이 적립돼요
        </p>
      </div>

      {/* 추천 링크 공유 (C-8-3) */}
      <Card className="mt-06">
        <p className="text-body-sm font-semibold text-text">내 추천 링크</p>
        {codeQuery.isLoading ? (
          <Skeleton className="mt-03" height={40} />
        ) : codeQuery.isError ? (
          <ErrorState className="mt-03" title="추천 링크를 불러오지 못했습니다" />
        ) : (
          <>
            <div className="mt-03 flex items-center gap-sm">
              <Input width="fill" value={shareUrl ?? ''} readOnly />
              <Button variant={copied ? 'secondary' : 'primary'} onClick={handleCopy}>
                {copied ? '복사됨!' : '복사'}
              </Button>
            </div>

            {/* 재발송(확인용) — 내 번호로 다시 받아보기. 실제 채널은 Hermes 정책이 결정(handleResend 참고). */}
            <div className="mt-03 flex gap-sm">
              <button
                type="button"
                onClick={() => handleResend('kakao')}
                disabled={resending !== null}
                className="text-body-sm font-semibold"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--gap-xs)',
                  flex: 1, height: 'var(--button-height-base)', borderRadius: 'var(--radius-md)',
                  border: 'none', cursor: resending !== null ? 'not-allowed' : 'pointer',
                  opacity: resending !== null && resending !== 'kakao' ? 0.6 : 1,
                  background: KAKAO_BRAND.bg, color: KAKAO_BRAND.label,
                }}
              >
                <span
                  className="text-caption font-bold"
                  style={{
                    width: 18, height: 18, borderRadius: 'var(--radius-sm)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    background: KAKAO_BRAND.iconBg, color: KAKAO_BRAND.iconColor,
                  }}
                >
                  K
                </span>
                {resending === 'kakao' ? '요청 중…' : '카카오톡'}
              </button>
              <Button
                variant="secondary"
                className="flex-1"
                disabled={resending !== null}
                onClick={() => handleResend('sms')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                {resending === 'sms' ? '요청 중…' : '문자'}
              </Button>
            </div>
            <p className="mt-02 text-caption text-text-muted">
              내 번호로 추천 링크를 다시 받아볼 수 있어요
            </p>
          </>
        )}
      </Card>

      {/* 혜택 안내 (C-8-4) */}
      <div className="mt-06 grid grid-cols-1 gap-md sm:grid-cols-2">
        <Card>
          <p className="text-body-sm font-semibold text-text">
            추천한 나는 — <span className="text-group">1개월 무료</span>
          </p>
          <p className="mt-04 text-caption leading-relaxed text-text-muted">
            추천받은 사장님이 유료로 전환하는 순간, 다음 결제일에 자동으로 한 달이 빠져요
          </p>
        </Card>
        <Card>
          <p className="text-body-sm font-semibold text-text">
            추천받은 사장님은 — <span className="text-group">체험 2개월</span>
          </p>
          <p className="mt-04 text-caption leading-relaxed text-text-muted">
            기본 1개월 무료체험에 1개월이 더 얹어져서, 총 2개월을 체험할 수 있어요
          </p>
        </Card>
      </div>

      <Card className="mt-06 bg-group-subtle">
        <p className="text-body-sm text-group-onSubtle">
          🎁 <span className="font-bold">3건 모두 유료 전환에 성공하면 3개월</span>을 통째로 더 드려요
        </p>
      </Card>

      {/* 추천 현황판 (C-8-5) */}
      <Card className="mt-06">
        <h3 className="text-title-sm text-text">추천 현황</h3>
        {statusQuery.isLoading ? (
          <Skeleton className="mt-04" height={120} />
        ) : statusQuery.isError ? (
          <ErrorState className="mt-04" title="추천 현황을 불러오지 못했습니다" />
        ) : (
          <>
            <div className="mt-04 flex items-center justify-between text-body-sm">
              <span className="text-text-muted">3건 달성 보너스까지</span>
              <span className="font-semibold text-text">
                {converted} / {target}건
              </span>
            </div>
            <div className="mt-02 flex gap-xs">
              {Array.from({ length: target }).map((_, i) => (
                <div
                  key={i}
                  className={`h-02 flex-1 rounded-full ${i < converted ? 'bg-group' : 'bg-border'}`}
                />
              ))}
            </div>
            <p className="mt-02 text-caption text-text-muted">
              {remaining > 0
                ? `${remaining}건만 더 성사되면 보너스 3개월이 추가로 적립돼요`
                : '3건 보너스 3개월이 적립됐어요'}
            </p>

            <div className="mt-04 flex flex-col divide-y divide-border">
              {statusQuery.data && statusQuery.data.invites.length > 0 ? (
                statusQuery.data.invites.map((invite, i) => <InviteRow key={i} invite={invite} />)
              ) : (
                <p className="py-04 text-body-sm text-text-muted">아직 초대한 사장님이 없어요</p>
              )}
            </div>

            <div className="mt-04 flex items-center justify-between border-t border-border pt-03">
              <span className="text-body-sm text-text-muted">지금까지 쌓은 무료 개월</span>
              <span className="text-body-md font-bold text-text">
                {statusQuery.data?.accumulatedFreeMonths ?? 0}개월
              </span>
            </div>
          </>
        )}
      </Card>
    </Page>
  );
}

function InviteRow({ invite }: { invite: ReferralInvite }) {
  const badge = STATUS_BADGE[invite.referralStatus] ?? { label: invite.referralStatus, status: 'info' as const };
  return (
    <div className="flex items-center gap-sm py-03">
      <Avatar>{invite.name.charAt(0)}</Avatar>
      <div className="min-w-0 flex-1">
        <p className="truncate text-body-sm font-semibold text-text">{invite.name}</p>
        <p className="text-caption text-text-muted">{formatDate(invite.joinedDtm)} 초대</p>
      </div>
      <Badge status={badge.status}>
        {badge.label}
      </Badge>
      <span className="text-caption text-text-muted">{invite.earnLabel}</span>
    </div>
  );
}
